import { flexRender, type Table } from "@tanstack/react-table";
import { ChevronIcon } from "vega-icons";

import {
  TableHeader,
  TableHeadV2,
  TableRow,
} from "@vega-core/components/shadcn/table";
import { type useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { usePinnedColumns } from "../hooks/usePinnedColumns";

type TableHeaderProps<TData> = {
  table: Table<TData>;
  noSorting?: boolean;
  headerClassName?: string;
  collapsible?: boolean;
  isLoading?: boolean;
  collapsed: ReturnType<typeof useBoolean>;
  pinnedLeftColumns?: string[];
  isScrolled?: boolean;
};

export function TableHeaderComponent<TData>({
  table,
  noSorting = false,
  headerClassName,
  collapsible,
  isLoading,
  collapsed,
  pinnedLeftColumns = [],
  isScrolled = false,
}: TableHeaderProps<TData>) {
  // this directive is needed to prevent memoization issues caused by incompatibility between React compiler and TanStack Table https://github.com/TanStack/table/issues/5567
  "use no memo";
  const { getPinnedColumnProps } = usePinnedColumns({
    pinnedLeftColumns,
    table,
  });

  return (
    <TableHeader data-testid="table-header">
      <TableRow>
        {table.getFlatHeaders().map((header) => {
          const {
            isPinned,
            isLastPinned,
            leftOffset,
            columnWidth,
            columnMaxSize,
            columnMinSize,
          } = getPinnedColumnProps(header.id);

          const headerTooltip = header.column.columnDef.headerTooltip ?? "";
          const cellHeaderClassName =
            header.column.columnDef.cellHeaderClassName ?? "";
          const noHeader = isFalsy(header.column.columnDef.header);

          const hideSorting =
            noSorting ||
            !header.column.getCanSort() ||
            header.column.columnDef.enableSorting === false;

          const isMobileSelector = header.column.columnDef.isMobileSelector;

          const headerContent = flexRender(
            header.column.columnDef.header,
            header.getContext(),
          );

          const shouldStick = isPinned && isScrolled;

          const align = header.column.columnDef.align ?? "left";

          return (
            <TableHeadV2
              align={align}
              className={cnTwMerge(
                {
                  "cursor-pointer select-none": !hideSorting,
                },
                cellHeaderClassName,
                headerClassName,
                shouldStick && "sticky z-10 bg-background/subtle",
              )}
              data-testid={`header-${header.id}`}
              hideSorting={hideSorting}
              isLastPinned={isLastPinned}
              isMobileSelector={isMobileSelector}
              isScrolled={isScrolled}
              key={header.id}
              noHeader={noHeader}
              onClick={() => {
                if (noSorting) return;

                if (!header.column.getCanSort()) return;
                header.column.toggleSorting();
              }}
              sortedHeader={header.column.getIsSorted()}
              style={
                shouldStick
                  ? {
                      left: leftOffset,
                      width: columnWidth,
                      minWidth: columnMinSize,
                      maxWidth: columnMaxSize,
                    }
                  : {
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                    }
              }
              tooltip={headerTooltip}
            >
              {headerContent}
            </TableHeadV2>
          );
        })}
        {isTruthy(collapsible) && isFalsy(isLoading) && (
          <th className="w-8 bg-background/secondary text-text/secondary cursor-pointer hover:text-text/primary">
            <ChevronIcon
              className={cnTwMerge({
                "rotate-0": collapsed.value,
                "rotate-[-180deg]": !collapsed.value,
              })}
              onClick={collapsed.toggle}
            />
          </th>
        )}
      </TableRow>
    </TableHeader>
  );
}
